import { useState } from 'react';
import { Link } from 'react-router';
import { textingApi, type RegistrationView } from '../../api/client';
import { Button, Card, Chip, Field, LoadError, Loading, Notice } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';
import { NUMBER_WORDS, STATE_WORDS } from '../tenant/TextingRegistration';

/**
 * Texting registration for Stitch staff (1.13.0, Teampace 3073): a company's registration as its
 * admins see it, with what only staff do: ask the carrier now, submit past the checks, hold or
 * release it, retire the campaign, link a registration made by hand at the carrier, and allow a
 * number to text anyway (with a reason, audited).
 */
export function RegistrationStaffCard({ customerId }: { customerId: string }) {
  const data = useData(() => textingApi.staffView(customerId), [customerId], { live: false });
  const [view, setView] = useState<RegistrationView | null>(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [brandId, setBrandId] = useState('');
  const [reason, setReason] = useState('');
  const v = view ?? data.data ?? null;

  async function act(what: string, fn: () => Promise<RegistrationView>) {
    setBusy(what);
    setError('');
    try {
      setView(await fn());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  }
  async function p2p(number: string, on: boolean, markOnly = false) {
    setBusy(number);
    setError('');
    try {
      const r = await textingApi.setP2p(customerId, number, on, markOnly);
      if (v) setView({ ...v, numbers: r.numbers });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  }
  async function allow(number: string, why: string | null) {
    setBusy(number);
    setError('');
    try {
      const r = await textingApi.allowNumber(customerId, number, why);
      if (v) setView({ ...v, numbers: r.numbers });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  }

  if (data.error) return <LoadError error={data.error} retry={data.reload} />;
  if (!v) return <Loading label="Loading texting registration" />;
  const r = v.registration;
  const words = STATE_WORDS[r.state];
  return (
    <Card title="Texting registration" action={<Chip tone={words.tone}>{words.label}</Chip>}>
      <div className="card-body section-stack">
        <p className="muted">
          {r.state === 'draft'
            ? 'Not submitted. The company’s admins register in Settings → Texting registration.'
            : `${r.brand || 'Brand'} · campaign ${r.crid ?? r.campaignId ?? '–'}${r.carrierStatus ? ` · ${r.carrierStatus}` : ''}${r.checkedAt ? ` · checked ${shortWhen(r.checkedAt)}` : ''}${r.imported ? ' · linked by hand' : ''}`}
        </p>
        {r.rejectReason && <Notice tone="crit" title="Turned down">{r.rejectReason}</Notice>}
        {r.error && <Notice tone="warn">{r.error}</Notice>}
        {error && <Notice tone="crit">{error}</Notice>}
        <div className="row-actions">
          <Button size="small" disabled={Boolean(busy)} onClick={() => void act('check', () => textingApi.staffAction(customerId, 'check'))}>
            Check with the carrier
          </Button>
          {v.editable && (
            <Button size="small" disabled={Boolean(busy) || !v.carrier?.spending} title="Sends it even if Leland’s checks don’t all pass" onClick={() => void act('submit', () => textingApi.staffAction(customerId, 'submit'))}>
              Submit anyway
            </Button>
          )}
          {r.held ? (
            <Button size="small" disabled={Boolean(busy)} onClick={() => void act('release', () => textingApi.staffAction(customerId, 'release'))}>
              Release
            </Button>
          ) : (
            <Button size="small" disabled={Boolean(busy)} onClick={() => void act('hold', () => textingApi.staffAction(customerId, 'hold', reason ? { reason } : {}))}>
              Hold
            </Button>
          )}
          {r.campaignId && r.state !== 'retired' && (
            <Button size="small" variant="danger" disabled={Boolean(busy)} onClick={() => void act('retire', () => textingApi.staffAction(customerId, 'retire'))}>
              Retire campaign
            </Button>
          )}
        </div>
        <div className="form-grid">
          <Field label="Brand id at the carrier (to link a registration made by hand)" value={brandId} onChange={(e) => setBrandId(e.target.value)} placeholder="Found by the company’s name when empty" />
          <Field label="Reason (to hold, or to allow a number)" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div>
          <Button size="small" disabled={Boolean(busy)} onClick={() => void act('import', () => textingApi.staffAction(customerId, 'import', brandId.trim() ? { brandId: brandId.trim() } : {}))}>
            Link from the carrier
          </Button>
        </div>
        {v.numbers.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Texts go to</th>
                  <th>Texting</th>
                  <th>
                    <span className="sr-only">Allow</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {v.numbers.map((n) => (
                  <tr key={n.number}>
                    <td>{n.label}</td>
                    <td>{n.where}</td>
                    <td>
                      <span className="cell-stack">
                        <Chip small tone={NUMBER_WORDS[n.state].tone}>
                          {NUMBER_WORDS[n.state].label}
                        </Chip>
                        {n.allowedReason && <small className="muted">{n.allowedReason}</small>}
                      </span>
                    </td>
                    <td className="right">
                      {!n.team && !n.p2p && (
                        <>
                          <Button size="small" variant="ghost" disabled={Boolean(busy)} title="Stitch asks Ahoi to turn on Person to Person SMS for it" onClick={() => void p2p(n.number, true)}>
                            Turn on P2P
                          </Button>
                          <Button size="small" variant="ghost" disabled={Boolean(busy)} title="It’s already on in Ahoi’s portal: mark it here without calling Ahoi" onClick={() => void p2p(n.number, true, true)}>
                            Already on at Ahoi
                          </Button>
                        </>
                      )}
                      {n.p2p && (
                        <>
                          <Button size="small" variant="ghost" disabled={Boolean(busy)} title="Stitch asks Ahoi to disable messaging on it: its texts stop at once" onClick={() => void p2p(n.number, false)}>
                            P2P off
                          </Button>
                          <Button size="small" variant="ghost" disabled={Boolean(busy)} title="It’s already off in Ahoi’s portal: mark it here without calling Ahoi" onClick={() => void p2p(n.number, false, true)}>
                            Already off at Ahoi
                          </Button>
                        </>
                      )}
                      {n.allowedReason ? (
                        <Button size="small" variant="ghost" disabled={Boolean(busy)} onClick={() => void allow(n.number, null)}>
                          Stop allowing
                        </Button>
                      ) : (
                        <Button size="small" variant="ghost" disabled={Boolean(busy) || reason.trim().length < 3} title="Needs a reason above" onClick={() => void allow(n.number, reason.trim())}>
                          Allow anyway
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

/** Every company's registration, for the Texting check page. */
export function RegistrationsListCard() {
  const data = useData(() => textingApi.all(), [], { live: false });
  const list = (data.data?.registrations ?? []).filter((x) => x.state || x.allowedAnyway);
  return (
    <Card title="Texting registrations">
      {data.error ? (
        <LoadError error={data.error} retry={data.reload} />
      ) : !data.data ? (
        <Loading label="Loading registrations" />
      ) : !list.length ? (
        <p className="card-body muted">No company has started a registration yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Registration</th>
                <th>Submitted</th>
                <th>Checked</th>
              </tr>
            </thead>
            <tbody>
              {list.map((x) => (
                <tr key={x.customerId}>
                  <td>
                    <Link to={`/admin/customers/${x.customerId}`}>{x.name}</Link>
                  </td>
                  <td>
                    <span className="cell-stack">
                      {x.state ? <Chip small tone={STATE_WORDS[x.state].tone}>{STATE_WORDS[x.state].label}</Chip> : <Chip small>Not started</Chip>}
                      {x.allowedAnyway && <small className="muted">Allowed by Stitch (registered outside Stitch)</small>}
                      {x.held && <small className="muted">Held: {x.heldReason}</small>}
                      {x.rejectReason && <small className="muted">{x.rejectReason}</small>}
                    </span>
                  </td>
                  <td>{x.submittedAt ? shortWhen(x.submittedAt) : '–'}</td>
                  <td>{x.checkedAt ? shortWhen(x.checkedAt) : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
